import { useCallback, useEffect, useMemo, useState } from "react";
import { CONFIGURATION_SERVICE, restApiRequest } from "src/utils/Api";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import useSchema from "./useSchema";
import parseDataFromBackend from "./parseDataFromBackend";
import applyLegalContentVariantsRule from "./applyLegalContentVariantsRule";
import filterLegalContentVariantsInSchema from "./filterLegalContentVariantsInSchema";
import adjustEmptyVariantValidation from "./adjustEmptyVariantValidation";

const useConfigurationFormState = (scope, excludeKeys) => {
  const [data, setData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [loadingData, setLoadingData] = useState(true);

  const { filteredSchema, loadingSchema } = useSchema(scope, excludeKeys);

  const fetchConfigRecords = useCallback(async () => {
    try {
      const params = {};
      if (scope.companyId) {
        params.companyId = scope.companyId;
      }
      if (scope.employeeGroupId) {
        params.employeeGroupId = scope.employeeGroupId;
      }
      setLoadingData(true);
      const res = await restApiRequest(
        CONFIGURATION_SERVICE,
        "/get-config-records",
        "GET",
        {
          params,
        },
        []
      );

      const raw = parseDataFromBackend(res);
      /* Scenario for codes-legal */
      const adjusted = applyLegalContentVariantsRule(raw);

      setOriginalData(raw);
      setData(adjusted);
    } catch (e) {
      newRelicErrorReport(
        e,
        "Pages/Administration/Configuration/utils/useConfigurationFormState.js - 47"
      );
      dynamicNotification(
        e.message || __("Nie udało się pobrać konfiguracji"),
        "error"
      );
    } finally {
      setLoadingData(false);
    }
  }, [scope.companyId, scope.employeeGroupId]);

  useEffect(() => {
    fetchConfigRecords();
  }, [fetchConfigRecords]);

  /* Scenario for codes-legal */
  const adjustedSchema = useMemo(
    () => filterLegalContentVariantsInSchema(filteredSchema, originalData),
    [filteredSchema, originalData]
  );

  const schemaWithDynamicValidation = useMemo(
    () => adjustEmptyVariantValidation(adjustedSchema, data),
    [adjustedSchema, data]
  );

  return {
    data,
    setData,
    originalData,
    loadingData,
    schema: schemaWithDynamicValidation,
    loadingSchema,
    adjustedData: data,
    reload: fetchConfigRecords,
  };
};

export default useConfigurationFormState;
